import { UserStore } from '@core/services/user/user.store'
import { IUser } from '@app/core/models/user.model'

export function getUserData(userStore: UserStore): IUser | null {
    return userStore.getUserData()
}

/**
 * Get the current user ID from UserStore
 * @param userStore - The UserStore instance
 */
export function getUserId(userStore: UserStore): string | undefined {
    return userStore.getUserData()?.user_id
}

export function getUserType(userStore: UserStore): string | undefined {
    return userStore.getUserData()?.user_type
}

/**
 * Get the user's permission code from UserStore
 */
export function getUserPermission(userStore: UserStore): string | undefined {
    return userStore.getUserData()?.permission
}

/**
 * Get the user's email from UserStore
 */
export function getUserEmail(userStore: UserStore): string | undefined {
    return userStore.getUserData()?.email ?? undefined
}

/**
 * Get the user's organisation code from UserStore
 */
export function getUserOrganisationCode(userStore: UserStore): number | undefined {
    return userStore.getUserData()?.organisation_code ?? undefined
}

export function getUserFullName(userStore: UserStore): string | undefined {
    const user = userStore.getUserData()
    if (!user?.name) return undefined

    const { first, middle, last } = user.name
    const parts = [first, middle, last].filter(Boolean)
    return parts.join(' ').trim() || undefined
}

export function getUserDisplayName(userStore: UserStore): string | undefined {
    const user = userStore.getUserData()
    if (!user?.name) return undefined

    const { first, last } = user.name
    const parts = [first, last].filter(Boolean)
    return parts.join(' ').trim() || undefined
}

export function getUserAvatarUrl(userStore: UserStore): string | undefined {
    return userStore.getUserData()?.avatar_url ?? undefined
}

/**
 * Check if user is logged in
 */
export function isUserLoggedIn(userStore: UserStore): boolean {
    return userStore.getUserData() !== null
}

/**
 * Get user details as an object with commonly used properties
 */
export function getUserDetails(userStore: UserStore): {
    userId: string
    userType: string
    permission: string
    email: string | null
    organisationCode: number | null
    fullName: string
    displayName: string
    avatarUrl: string | null
    timezone: string
} | null {
    const user = userStore.getUserData()
    if (!user) return null

    const { first, middle, last } = user.name
    const nameParts = [first, middle, last].filter(Boolean)
    const displayParts = [first, last].filter(Boolean)

    return {
        userId: user.user_id,
        userType: user.user_type,
        permission: user.permission,
        email: user.email,
        organisationCode: user.organisation_code,
        fullName: nameParts.join(' ').trim(),
        displayName: displayParts.join(' ').trim(),
        avatarUrl: user.avatar_url,
        timezone: user.timezone,
    }
}
